import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import { Button } from '../components/ui/Button';
import Chip from '../components/ui/Chip';
import Dialog from '../components/ui/Dialog';
import FadeIn from '../components/ui/FadeIn';
import { Field } from '../components/ui/Field';
import { IconPlus } from '../components/ui/icons';
import { MS, usePresence } from '../components/ui/motion';
import Reveal from '../components/ui/Reveal';
import SegTrack from '../components/ui/SegTrack';
import Tooltip from '../components/ui/Tooltip';
import { createProduction } from './sparkApi';
import type { EventHit, NewProject } from './sparkApi';
import { SpiceMeter } from './SparkBits';
import { EventLinkPicker } from './SparkEventLinks';
import { CHANNELS, RUNWAYS, SPECIALITIES, SPICE, WORK_STEPS } from './sparkRules';
import type { Channel, Runway, Speciality } from './sparkRules';
import { dayText, errorMessage } from './sparkUi';

// The project just created, so the list can light it up when the user comes back to it,
// by the back button or the back link alike. Ten minutes later it is no longer news.
let landed: { id: string; at: number } | null = null;
const LANDED_MS = 10 * 60 * 1000;

/** The id of a project created moments ago, if it is among `ids`; reading it uses it up. */
export const takeLanded = (ids: string[]): string | null => {
  if (!landed) return null;
  if (Date.now() - landed.at > LANDED_MS) { landed = null; return null; }
  if (!ids.includes(landed.id)) return null;
  const { id } = landed;
  landed = null;
  return id;
};

const LABEL = 'block text-[13px] font-medium leading-[18px] text-on-surface';

const Row: React.FC<{ label: React.ReactNode; htmlFor?: string; hint?: string; children: React.ReactNode }> = ({ label, htmlFor, hint, children }) => (
  <div className="min-w-0">
    {htmlFor ? <label htmlFor={htmlFor} className={LABEL}>{label}</label> : <span className={LABEL}>{label}</span>}
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1.5 text-xs text-on-surface-variant">{hint}</p>}
  </div>
);

const ProjectForm: React.FC<{ defaultChannel: Channel; onCancel: () => void; open: boolean }> = ({ defaultChannel, onCancel, open }) => {
  const toast = useToast();
  const history = useHistory();
  const [name, setName] = useState('');
  const defaultSpeciality: Speciality = defaultChannel === 'marketing' ? 'marketing' : defaultChannel === 'systems' ? 'systems' : 'shows';
  const [speciality, setSpeciality] = useState<Speciality>(defaultSpeciality);
  const [channel, setChannel] = useState<Channel>(defaultChannel);
  const [steps, setSteps] = useState<string[]>([]);
  const [runway, setRunway] = useState<Runway>('standard');
  const [spice, setSpice] = useState(2);
  const [showDate, setShowDate] = useState('');
  const [client, setClient] = useState('');
  const [event, setEvent] = useState<EventHit | null>(null);
  const [pickingEvent, setPickingEvent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const linkRef = useRef<HTMLButtonElement>(null);
  const removeRef = useRef<HTMLButtonElement>(null);
  // The control that replaces the event picker takes focus when it closes, or focus falls to <body>.
  const refocus = useRef<'link' | 'remove' | null>(null);
  useEffect(() => {
    const to = refocus.current;
    refocus.current = null;
    if (to === 'link') linkRef.current?.focus();
    else if (to === 'remove') removeRef.current?.focus();
  }, [event, pickingEvent]);
  const closePicker = () => { refocus.current = 'link'; setPickingEvent(false); };
  const pickEvent = (h: EventHit) => { refocus.current = 'remove'; setEvent(h); setPickingEvent(false); };
  const unlinkEvent = () => { refocus.current = 'link'; setEvent(null); };

  // The channel follows the speciality until someone picks one by hand.
  const [channelTouched, setChannelTouched] = useState(false);
  const pickSpeciality = (key: string) => {
    const s = SPECIALITIES.find((x) => x.key === key);
    if (!s) return;
    setSpeciality(s.key);
    if (!channelTouched) setChannel(s.channel);
  };

  const toggleStep = (key: string) =>
    setSteps((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const isSystems = speciality === 'systems';
  const isMarketing = speciality === 'marketing';
  const isShows = channel === 'shows';
  const problem = !name.trim()
    ? 'Give the project a name.'
    : isSystems && steps.length === 0
      ? 'Pick at least one work step. Each becomes its own deliverable.'
      : '';
  const spiceInfo = SPICE.find((s) => s.level === spice);

  const submit = async () => {
    setTriedSubmit(true);
    if (problem || busy) return;
    setBusy(true);
    const body: NewProject = {
      name: name.trim(),
      speciality,
      channel,
      spice,
      ...(isMarketing && steps.length ? { work_steps: steps } : {}),
      ...(isSystems ? { work_steps: steps, client_name: client.trim() || null } : {}),
      ...(isShows ? { runway } : {}),
      ...(event ? { event_id: event.id } : { show_date: showDate || null }),
    };
    try {
      const r = await createProduction(body);
      landed = { id: r.id, at: Date.now() };
      history.push(`/spark/projects/${r.id}`, { offer: r.offer || null });
    } catch (err) {
      toast.error(errorMessage(err, 'Could not create the project. Check the fields and try again.'));
      setBusy(false);
    }
  };

  const cancel = () => { if (!busy) onCancel(); };

  // The form mounts fresh on every opening, so its starting values are the defaults above.
  const dirty = name !== '' || speciality !== defaultSpeciality || channel !== defaultChannel
    || steps.length > 0 || runway !== 'standard' || spice !== 2 || showDate !== '' || client !== '' || event !== null;

  const dateLabel = isShows ? 'Show date' : 'Due date';
  const showProblem = triedSubmit && !!problem;

  return (
    <Dialog
      open={open}
      title="New project"
      size="lg"
      closeHint="Close without creating"
      busy={busy}
      dirty={dirty}
      onClose={cancel}
      onSubmit={submit}
      problem={showProblem ? problem : undefined}
      footer={(
        <>
          <Tooltip label="Close without creating">
            <Button variant="tonal" disabled={busy} onClick={cancel}>Cancel</Button>
          </Tooltip>
          <Tooltip label="Create it and open the project">
            <Button type="submit" variant="filled" disabled={busy}>{busy ? 'Creating' : 'Create project'}</Button>
          </Tooltip>
        </>
      )}
    >
      <div className="space-y-5 pt-1">
            <Row label="Name" htmlFor="np-name">
              <Field
                id="np-name"
                autoFocus
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
                placeholder="What is it called?"
                error={triedSubmit && !name.trim()}
              />
            </Row>

            <Row label="Speciality">
              <SegTrack
                label="Speciality"
                fill
                value={speciality}
                onChange={pickSpeciality}
                options={SPECIALITIES.map((s) => ({ key: s.key, label: s.label }))}
              />
            </Row>

            <Row label="Channel" hint="Follows the speciality. Change it if the work belongs elsewhere.">
              <SegTrack
                label="Channel"
                value={channel}
                onChange={(k) => { setChannel(k as Channel); setChannelTouched(true); }}
                options={CHANNELS.map((c) => ({ key: c.key, label: c.label }))}
              />
            </Row>

            <Reveal show={isSystems || isMarketing}>
              <Row
                label="Work steps"
                hint={isMarketing
                  ? (steps.includes('Motion Graphics')
                    ? 'Starts with the Motion Graphics deliverables; any other step adds one of its own.'
                    : 'Optional. Without Motion Graphics it starts with the Shows deliverables; each step adds one.')
                  : steps.length ? `Deliverables, in the order you picked: ${steps.join(', ')}.` : 'Pick one or more. Each becomes its own deliverable.'}
              >
                <div role="group" aria-label="Work steps" className="flex flex-wrap gap-1.5">
                  {WORK_STEPS.map((w) => (
                    <Chip key={w} pressed={steps.includes(w)} onClick={() => toggleStep(w)}>{w}</Chip>
                  ))}
                </div>
              </Row>
            </Reveal>

            <Reveal show={isSystems}>
              <div className="space-y-5 p-px">
                <Row label="Client" htmlFor="np-client">
                  <Field
                    id="np-client"
                    value={client}
                    maxLength={200}
                    onChange={(e) => setClient(e.target.value)}
                    placeholder="Who is it for?"
                    className="sm:max-w-sm"
                  />
                </Row>
              </div>
            </Reveal>

            <Reveal show={isShows}>
              <Row label="Runway" hint="How long before the show the preview is due.">
                <SegTrack
                  label="Runway"
                  fill
                  value={runway}
                  onChange={(k) => setRunway(k as Runway)}
                  options={RUNWAYS.map((r) => ({ key: r.key, label: r.label, sub: `${r.days} days` }))}
                />
              </Row>
            </Reveal>

            <Row
              label={(
                <span className="flex items-center gap-2">
                  Spice
                  <SpiceMeter level={spice} focusable={false} />
                </span>
              )}
              hint={spiceInfo ? `${spiceInfo.short}. ${spiceInfo.reference}.` : undefined}
            >
              <SegTrack
                label="Spice"
                fill
                value={String(spice)}
                onChange={(k) => setSpice(Number(k))}
                options={SPICE.map((s) => ({ key: String(s.level), label: s.label }))}
              />
            </Row>

            <div className="grid gap-5 sm:grid-cols-2">
              <Row label="Event" hint="Optional. Link one now or later from the project.">
                {event ? (
                  <FadeIn key="picked" className="flex h-8 items-center gap-2 rounded-control bg-surface-container pl-2.5">
                    <span className="min-w-0 flex-1 truncate text-sm text-on-surface">
                      {event.name}
                      <span className="ml-2 text-xs tabular-nums text-on-surface-variant">{dayText(event.event_date)}</span>
                    </span>
                    <Tooltip label="Unlink this event from the new project">
                      <Button ref={removeRef} variant="quiet" size="sm" onClick={unlinkEvent}>Remove</Button>
                    </Tooltip>
                  </FadeIn>
                ) : pickingEvent ? (
                  <FadeIn key="picking" className="rounded-control bg-surface-container p-2.5">
                    <EventLinkPicker autoFocus onPick={pickEvent} onCancel={closePicker} />
                    <Button variant="quiet" size="sm" className="mt-2" onClick={closePicker}>Cancel</Button>
                  </FadeIn>
                ) : (
                  <FadeIn key="idle">
                    <Tooltip label="Search events by name or place">
                      <Button ref={linkRef} variant="tonal" leading={<IconPlus className="h-3.5 w-3.5" />} onClick={() => setPickingEvent(true)}>Link an event</Button>
                    </Tooltip>
                  </FadeIn>
                )}
              </Row>

              {event ? (
                <Row label={dateLabel}>
                  <FadeIn key="from-event">
                    <p className="m-0 flex h-8 items-center text-sm text-on-surface-variant">From the event, {dayText(event.event_date)}.</p>
                  </FadeIn>
                </Row>
              ) : (
                <Row label={dateLabel} htmlFor="np-date" hint="Optional. Deliverables and the preview date are counted from it.">
                  <Field
                    id="np-date"
                    type="date"
                    value={showDate}
                    onChange={(e) => setShowDate(e.target.value)}
                    className="sm:max-w-[12rem]"
                  />
                </Row>
              )}
            </div>
      </div>
    </Dialog>
  );
};

/**
 * The new project dialog: each opening starts a fresh form, because the form unmounts once the
 * dialog has left. Dialog owns the scrim, focus and the click outside, which goes through the
 * same cancel that holds while a create is in flight.
 */
const NewProjectForm: React.FC<{ open: boolean; defaultChannel: Channel; onCancel: () => void }> = ({ open, defaultChannel, onCancel }) => {
  const { mounted } = usePresence(open, MS.exit);
  if (!mounted) return null;
  return <ProjectForm defaultChannel={defaultChannel} onCancel={onCancel} open={open} />;
};

export default NewProjectForm;
